import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY } from 'rxjs';
import { catchError, retry, shareReplay } from 'rxjs/operators';
import { CertArea, CertAreaDTO } from 'src/app/shared/models/certArea.model';
import { ContactInfo } from 'src/app/shared/models/contact-info.model';
import { ConfigService } from '../services/config.service';
import { Education } from '../shared/models/education.model';
import { Email, Intern } from '../shared/models/intern.model';
import { Reason } from '../shared/models/reason.model';
import { Observable } from 'rxjs';
import { SearchParameter } from '../shared/enums/search-parameter.enum';
import { Result } from '../shared/models/result.model';
import { StateId } from '../shared/enums/state-id.enum';
import {CandidateSearchInfo} from "../shared/models/candidate-search-info.model";
import { AuthService } from './auth.service';



@Injectable({
  providedIn: 'root'
})
export class InternService {

  headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  apiUrl = '';

  constructor(private httpClient: HttpClient,
    private configSvc: ConfigService,
    private authservice: AuthService) {
    this.apiUrl = this.configSvc.getApiUrl();
  }

    public setStatus(id: number, statusId: number, modUser: string, disqualificationReasonId?: number, reason: Reason = null): Observable<any> {
    const setStatus = new SetStatus();
    setStatus.modUser = modUser;
    setStatus.statusId = statusId;
    setStatus.disqualificationReasonId = disqualificationReasonId;
    setStatus.reasonId = reason?.id
    return this.httpClient.put(`${this.apiUrl}/setStatus/${id}`, JSON.stringify(setStatus));
  }

  public getList() {
    return this.httpClient.get<Intern[]>(`${this.apiUrl}/intern`).pipe(
      retry(3),
      catchError(error => {
        console.log(error);

        return EMPTY;
      }),
      shareReplay()
    );
  }

  public getGeneralInfo(profileId: any, internID: string = null, userEmail: string = null) {
    let url = `${this.apiUrl}/generalinfo/${profileId}`;

    if (!!userEmail && !!internID){
      url = `${url}?internID=${internID}&userEmail=${userEmail}`;
    }
    else if (!!internID){
      url = `${url}?internID=${internID}`;
    }
    else if (!!userEmail){
      url = `${url}?userEmail=${userEmail}`;
    }
    return this.httpClient.get<Intern>(url).pipe(
      retry(3),
      catchError(error => {
        console.log(error);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  public GetGeneralInfoByCandidateID(id: any, userEmail: string) {
    return this.httpClient.get<Intern>(`${this.apiUrl}/GeneralInfo_ByCandidateID/${id}?userEmail=${userEmail}`).pipe(
      retry(3),
      catchError(error => {
        console.log(error);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  public post(intern: Intern) {
    return this.httpClient.post<Intern>(this.apiUrl + "/generalinfo/", intern, { headers: this.headers });
  }

  public put(intern: Intern, id: string) {
    return this.httpClient.put<Result>(this.apiUrl + "/generalinfo/" + id, intern, { headers: this.headers });
  }

  public search(searchTerm: string) {
    return this.httpClient.get<Intern[]>(`${this.apiUrl}/v1.0/profile?q=${searchTerm}&userEmail=${this.authservice.user.email}`).pipe(
      catchError(error => {
        console.log(error);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  searchBy(searchTerm: string, searchBy: SearchParameter) {
    const queryParamName = SearchParameter[searchBy].toLocaleLowerCase();
    return this.httpClient.get<Intern[]>(`${this.apiUrl}/v1.0/profile?${queryParamName}=${searchTerm}&userEmail=${this.authservice.user.email}`).pipe(
      catchError(error => {
        console.log(error);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  public UpdateCandidateStatus(profileId: number, status) {
    return this.httpClient.post<any>(`${this.apiUrl}/v1.0/profile/UpdateStatus/${profileId}/${status}`, profileId, status).pipe(
      retry(0),
      catchError(error => {
        console.log(error);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  public getHighestDegreeConferred(candidateId: number) {
    return this.httpClient.get<Education>(`${this.apiUrl}/candidate/GetHighestDegreeConferred/${candidateId}`).pipe(
      retry(3),
      catchError(error => {
        return EMPTY;
      }),
      shareReplay()
    );
  }

  public getContactInfo(profileId: number) {
    return this.httpClient.get<ContactInfo>(`${this.apiUrl}/intern/contact/${profileId}`);
  }

  public getCertAreas(profileId: number) {
    return this.httpClient.get<CertAreaDTO[]>(`${this.apiUrl}/certification/areas/${profileId}`);
  }

  public getSmkCertAreas(profileId: number) {
    return this.httpClient.get<CertAreaDTO[]>(`${this.apiUrl}/CandidateCertification/GetSmkCertAreas/${profileId}`);
  }

  public getCertAreasByStateId(stateId: StateId, companyId: number) {
    return this.httpClient.get<CertArea[]>(`${this.apiUrl}/CandidateCertification/GetCertAreas?stateId=${stateId}&companyId=${companyId}`)
    .pipe(
      catchError(() => {return EMPTY}),
      shareReplay()
    );
  }

  public primaryEmailAlreadyExists(emailAddress: string, companyId: number): Observable<Email> {
    return this.httpClient.get<Email>(`${this.configSvc.apiUrl}/intern/emails?email=${emailAddress}&companyId=${companyId}`).pipe(
      retry(3),
      catchError(error => {
        console.log(error);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  public searchCandidates(searchTerm: string, companyId: number | null) {
    const companySearch = companyId ? `&companyId=${companyId}` : '';
    return this.httpClient.get<any[]>(`${this.apiUrl}/candidate-search?q=${searchTerm}${companySearch}`).pipe(
      retry(3),
      catchError(error => {
        console.log(error);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  public searchCandidatesForSponsorship(searchTerm: string, sponsorshipId: number) {
    return this.httpClient.get<CandidateSearchInfo[]>(`${this.apiUrl}/v1.0/sponsorship-candidates/sponsorship/${sponsorshipId}/?q=${searchTerm}`).pipe(
      retry(3),
      catchError(error => {
        console.log(error);
        return EMPTY;
      }),
      shareReplay()
    );
  }
}

class SetStatus {
  modUser = '';
  statusId = -1;
  disqualificationReasonId?: number;
  reasonId: number;
}

class SetStatuses {
  modUser = '';
  statusId = -1;
  financialStatusId = -1;
  sendUpdateTo2CP = false;
}
